import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Typography, Table, Tag, Space, Button, List, Row, Col, Select, Checkbox, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { AvailabilityDrawer, onLeave } from './AvailabilityPage';
import { PublishModal } from './ShiftSwapExplorePage';

// Oversight > Attendance & Schedule > Shift Board -- phase 1, admin only and
// view only (routes/shift_board.py): each upcoming absence that is not
// canceled, the Paycor shifts it overlaps, the WFM skills each shift needs,
// and who could cover or swap it. Nothing here changes a schedule.
const fmtTime = (iso) => dayjs(iso).format('h:mm A');
const fmtSpan = (s) => `${dayjs(s.Start).format('ddd MM/DD')} ${fmtTime(s.Start)} – ${fmtTime(s.End)}`;
const fmtMins = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

// WFM Availability: how the time sits in the person's availability that
// weekday ('HH:MM' window; a stop before the start runs past midnight).
const shortTime = (t) => {
  const d = dayjs(`2000-01-01T${t}`);
  return d.format(d.minute() ? 'h:mma' : 'ha');
};
const FIT = {
  yes:     { color: 'green',   label: 'Availability' },
  partly:  { color: 'gold',    label: 'Partial Availability' },
  no:      { color: 'red',     label: 'Not available' },
  unknown: { color: 'default', label: 'Availability not set' },
};
// Could Swap: whether their availability covers the whole shift (a swap
// takes all of it), as a sentence. The times are their availability, not
// what they would cover.
const FitTag = ({ p }) => {
  const f = FIT[p.Fit] || FIT.unknown;
  const win = p.AvailStart && p.AvailEnd ? `${shortTime(p.AvailStart)}–${shortTime(p.AvailEnd)}` : null;
  const n = first(p.Name);
  const text = {
    yes:     `${n} has Full Availability${win ? ` (${win})` : ''} to cover the entire shift`,
    partly:  `${n} has only Partial Availability${win ? ` (available ${win})` : ''}; cannot cover the entire shift`,
    no:      `${n} is not available during this shift${win ? ` (available ${win})` : ''}`,
  }[p.Fit] || `${n} has no availability entered for this day`;
  return <div style={{ marginTop: 2 }}><Tag color={f.color}>{text}</Tag></div>;
};
// Could Cover: what their availability lets them cover of the missed time,
// then their availability and preferences on one line.
const first = (name) => (name || '').split(' ')[0];
const hm = (iso) => dayjs(iso).format(dayjs(iso).minute() ? 'h:mma' : 'ha');
// missed: the time of the absence on this shift, e.g. "3pm to 10pm";
// missedMins: its length.
const hrsOf = (mins) => `${Math.round((mins / 60) * 100) / 100} hrs`;
const CoverLine = ({ c, missed, missedMins }) => {
  const win = c.AvailStart && c.AvailEnd ? `${shortTime(c.AvailStart)}–${shortTime(c.AvailEnd)}` : null;
  const status = {
    yes:     <Tag color="green">Could Cover Entire Absence{missed ? ` ${missed}` : ''}</Tag>,
    partly:  <Tag color="gold">{c.CoverFrom
      ? `Could Cover only ${hrsOf(dayjs(c.CoverTo).diff(dayjs(c.CoverFrom), 'minute'))} (${hm(c.CoverFrom)} – ${hm(c.CoverTo)}) of absence${missedMins ? ` of ${hrsOf(missedMins)}` : ''}`
      : 'Could Cover only part of Absence'}</Tag>,
    no:      <Tag color="red">No Availability to cover absence</Tag>,
    unknown: <Tag>Availability not set</Tag>,
  }[c.Fit] || <Tag>Availability not set</Tag>;
  // Partly with nothing entered: their schedule allows part of it.
  const avail = c.Fit === 'unknown' ? null
    : c.AvailSet === false ? 'Availability not set'
      : (win ? `Availability ${win}` : 'Not available that day');
  const parts = [avail, c.Preferences?.length ? `Prefers ${c.Preferences.join(', ')}` : null].filter(Boolean);
  return (
    <>
      <div style={{ marginTop: 2 }}>{status}</div>
      {parts.length > 0 && <div><Typography.Text style={{ fontSize: 13 }}>{parts.join(' / ')}</Typography.Text></div>}
      <AvailNote p={c} />
    </>
  );
};

// The WFM skills the shift needs that they do not have.
const Missing = ({ p }) => (p.MissingSkills?.length
  ? <Tag color="red" style={{ marginLeft: 6 }}>Missing: {p.MissingSkills.join(', ')}</Tag>
  : null);

// The absent person's WFM Availability for the week, their shift's day
// outlined, then their preferences (also the Shift Swap Forum's, with its
// own title).
const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const AbsentAvailability = ({ a, day, title }) => {
  const byDay = Object.fromEntries((a.Availability || []).map((d) => [d.Day, d]));
  return (
    <div>
      <Typography.Text strong>{title ?? `${a.Name}'s availability`}</Typography.Text>
      <div style={{ marginTop: 4 }}>
        <Space size={[4, 4]} wrap>
          {WEEK.map((w, i) => {
            const d = byDay[i + 1];
            const text = !d ? 'Not set'
              : d.Available && d.Start && d.End ? `${shortTime(d.Start)}–${shortTime(d.End)}` : 'Not available';
            return (
              <Tag key={w} color={d?.Available ? 'green' : undefined}
                style={i + 1 === day ? { fontWeight: 600, outline: '1px solid currentColor' } : undefined}>
                {w} {text}{d?.Notes ? ` · ${d.Notes}` : ''}
              </Tag>
            );
          })}
        </Space>
      </div>
      {a.Preferences?.length > 0 && (
        <Typography.Text style={{ fontSize: 13 }}>Prefers {a.Preferences.join(', ')}</Typography.Text>
      )}
    </div>
  );
};

// Their WFM Availability note for that day ("Also 1am-10am").
const AvailNote = ({ p }) => (p.AvailNote
  ? <div><Typography.Text style={{ fontSize: 13 }}>Note: {p.AvailNote}</Typography.Text></div>
  : null);

const Prefs = ({ p }) => (p.Preferences?.length
  ? <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Prefers {p.Preferences.join(', ')}</Typography.Text></div>
  : null);

// The checkboxes as filters on a shift's Cover and Swap lists.
export function boardFilters(onlyAvailable, allSkills, hideLeave) {
  const full = (x) => !x.MissingSkills?.length;
  const keep = (list) => list.filter((c) => (!onlyAvailable || c.Fit === 'yes' || c.Fit === 'partly') && (!allSkills || full(c))
    && (!hideLeave || !c.OnLeave));
  // A swap goes both ways: each shift offered back is kept only when the
  // absent person matches it too (availability and skills, as ticked).
  const offerOk = (x) => (!onlyAvailable || x.AbsentFit === 'yes' || x.AbsentFit === 'partly') && (!allSkills || full(x));
  const keepSwap = (list) => keep(list).map((c) => ({ ...c, Shifts: c.Shifts.filter(offerOk) }))
    .filter((c) => c.Shifts.length);
  return { keep, keepSwap };
}

// onOpen (when given): the name opens their WFM Availability / Skills panel.
const person = (p, onOpen) => (
  <span>{onOpen ? <Typography.Link onClick={() => onOpen(p.UserID)}>{p.Name}</Typography.Link> : p.Name} <Typography.Text style={{ fontSize: 13 }}>{[p.TeamName, p.JobName].filter(Boolean).join(' / ')}</Typography.Text>{onLeave(p)}</span>
);

// One shift's Could cover / Could swap (a: the absent person, s: the shift),
// also used by Schedule Break & Lunch's "Who could cover this shift?" and the
// Shift Swap Forum's explore (showCover false: Could swap alone).
export function CoverSwapLists({ a, s, onlyAvailable, allSkills, hideLeave, onOpen, showCover = true }) {
  const { keep, keepSwap } = boardFilters(onlyAvailable, allSkills, hideLeave);
  return (
    <Row gutter={24}>
      <Col span={24} style={{ marginBottom: 12 }}>
        <AbsentAvailability a={a} day={dayjs(s.Start).day() || 7} />
      </Col>
      {showCover && (
      <Col xs={24} md={10}>
        <Typography.Text strong>Could cover ({keep(s.Cover).length})</Typography.Text>
        {/* Their own shifts that day and paid hours, so someone already
            working a long day is not picked without seeing it. */}
        <List size="small" dataSource={keep(s.Cover)} locale={{ emptyText: '—' }} renderItem={(c) => (
          <List.Item>
            <div>
              {person(c, onOpen)}
              <Missing p={c} />
              {c.AbsentThatDay?.length > 0 && (
                <Tag color="orange" style={{ marginLeft: 6 }}>Absence that day: {c.AbsentThatDay.join(', ')}</Tag>
              )}
              <CoverLine c={c} missed={s.MissedStart ? `${hm(s.MissedStart)} to ${hm(s.MissedEnd)}` : null} missedMins={s.OverlapMinutes} />
              <div>
                {/* e.g. "Already scheduled 8 hrs 2:30PM - 11:00PM ConHome" (each piece of a split shift). */}
                {c.DayShifts?.length ? (
                  <Typography.Text style={{ fontSize: 13 }}>
                    Already scheduled{c.DayHours != null ? ` ${c.DayHours} hrs` : ''}{' '}
                    {c.DayShifts.map((x) => `${dayjs(x.Start).format('h:mmA')} - ${dayjs(x.End).format('h:mmA')} ${x.Department}`).join(', ')}
                  </Typography.Text>
                ) : <Typography.Text style={{ fontSize: 13 }}>Not scheduled that day</Typography.Text>}
              </div>
              {/* Their week (Monday to Sunday), less their absences: room before overtime. */}
              {c.WeekHours != null && (
                <div><Typography.Text style={{ fontSize: 13 }}>
                  Scheduled {c.WeekHours} hrs
                  {c.WeekAbsent > 0 && ` (${c.WeekScheduled} hrs less ${c.WeekAbsent} hrs absences)`}
                </Typography.Text></div>
              )}
            </div>
          </List.Item>
        )} />
      </Col>
      )}
      <Col xs={24} md={showCover ? 14 : 24}>
        <Typography.Text strong>Could swap ({keepSwap(s.Swap).length})</Typography.Text>
        <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Green: fits {first(a.Name)}&apos;s availability · Orange: partly fits · Red: outside it
        </Typography.Text></div>
        <List size="small" dataSource={keepSwap(s.Swap)} locale={{ emptyText: '—' }}
          renderItem={(c) => (
            <List.Item>
              <div>
                {person(c, onOpen)}
                <Missing p={c} />
                <FitTag p={c} />
                <Prefs p={c} />
                <AvailNote p={c} />
                <div><Typography.Text style={{ fontSize: 13 }}>{first(a.Name)} takes one of {first(c.Name)}&apos;s shifts:</Typography.Text></div>
                <div>
                  <Space size={[4, 4]} wrap>
                    {/* Each shift offered back: is the absent person available for it. */}
                    {c.Shifts.map((x) => (
                      <Tag key={x.Start} color={x.AbsentFit === 'yes' ? 'green' : x.AbsentFit === 'no' ? 'red' : x.AbsentFit === 'partly' ? 'gold' : undefined}
                        title={`${a.Name}: ${(FIT[x.AbsentFit] || FIT.unknown).label}${x.AbsentNote ? ` (Note: ${x.AbsentNote})` : ''}${x.MissingSkills?.length ? `; missing ${x.MissingSkills.join(', ')}` : ''}`}>
                        {fmtSpan(x)} · {x.Department}{x.MissingSkills?.length > 0 && ` · ${a.Name} missing: ${x.MissingSkills.join(', ')}`}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>
            </List.Item>
          )} />
      </Col>
    </Row>
  );
}

export default function ShiftBoardPage() {
  const currentUser = useContext(UserContext);
  // The absent person's WFM Availability and WFM Skills panel (the WFM
  // Availability page's), for those who can view WFM Skills.
  const canWfm = currentUser?.Role === 'admin' || (currentUser?.CanEditWFMSkills ?? 0) >= 1;
  const [personFor, setPersonFor] = useState(null);
  const [availPrefs, setAvailPrefs] = useState([]);
  const openPerson = (id) => {
    setPersonFor(id);
    if (!availPrefs.length) {
      api.get('/api/availability/preferences').then((r) => setAvailPrefs(r.data || [])).catch(() => {});
    }
  };
  const [data, setData]       = useState({ rows: [] });
  const [loading, setLoading] = useState(true);
  // Team: the absent person's team, from the user's Visible Teams.
  const [teams, setTeams]     = useState([]);
  const [teamId, setTeamId]   = useState(null);
  // Only those whose WFM Availability covers all or part of the time (not a
  // rule: a filter).
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  // Only those with every skill the shift needs (and, for a swap, the absent
  // person with every skill of the shift offered back). Unticked: anyone
  // eligible by team and job, with what they are missing.
  const [allSkills, setAllSkills] = useState(true);
  // Leaves out anyone on leave in Paycor (with or without pay).
  const [hideLeave, setHideLeave] = useState(true);
  const { keep, keepSwap } = boardFilters(onlyAvailable, allSkills, hideLeave);
  useEffect(() => {
    api.get('/api/teams', { params: { my_teams: 1, no_client_users: 1 } }).then((r) => setTeams(r.data || [])).catch(() => setTeams([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/shift-board', { params: teamId ? { team_id: teamId } : {} }).then((r) => setData(r.data))
      .catch(() => { setData({ rows: [] }); message.error('Failed to load the shift board.'); })
      .finally(() => setLoading(false));
  }, [teamId]);
  useEffect(() => { load(); }, [load]);

  // Shift Swap Forum (prototype): post an absence's shift to the forum; the
  // shifts already on it (open) show as such.
  const canPost = !!currentUser?.ShiftSwapForum;
  const [posted, setPosted] = useState(new Set());
  const [posting, setPosting] = useState(null);
  const loadPosted = useCallback(() => {
    if (!canPost) return;
    api.get('/api/shift-swap/posts').then((r) => setPosted(new Set((r.data.posts || []).map((p) => `${p.UserID}|${p.Start}`))))
      .catch(() => setPosted(new Set()));
  }, [canPost]);
  useEffect(() => { loadPosted(); }, [loadPosted]);

  // One row per affected shift; an absence that affects none still shows once.
  const rows = useMemo(() => data.rows.flatMap((a) => (a.Shifts.length
    ? a.Shifts.map((s, i) => ({ key: `${a.AbsenceID}|${i}`, a, s }))
    : [{ key: `${a.AbsenceID}|none`, a, s: null }])), [data]);

  const absent = (a) => (a.StartTime && a.EndTime ? `${a.StartTime} – ${a.EndTime}` : 'Entire day');

  return (
    <>
      <Space align="center" wrap style={{ marginBottom: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>WFM Absence Swap List</Typography.Title>
        <Select style={{ width: 240 }} placeholder="All teams" allowClear showSearch optionFilterProp="label"
          value={teamId} onChange={(v) => setTeamId(v ?? null)}
          options={teams.map((t) => ({ value: t.ID, label: t.Name }))} />
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        <Checkbox checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)}>Only matching availability</Checkbox>
        <Checkbox checked={allSkills} onChange={(e) => setAllSkills(e.target.checked)}>Only matching all skills</Checkbox>
        <Checkbox checked={hideLeave} onChange={(e) => setHideLeave(e.target.checked)}>Hide people on leave</Checkbox>
      </Space>
      {data.from && (
        <Typography.Paragraph style={{ marginBottom: 12 }}>
          Absences {dayjs(data.from).format('MM/DD/YYYY')} – {dayjs(data.to).format('MM/DD/YYYY')} that take at
          least {data.min_minutes} minutes of a shift.
          {data.schedule_loaded_at && ` Paycor schedule last loaded ${dayjs(data.schedule_loaded_at).format('MM/DD/YYYY h:mm A')}.`}
        </Typography.Paragraph>
      )}
      <Table
        rowKey="key"
        dataSource={rows}
        loading={loading}
        size="small"
        pagination={false}
        sticky
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No upcoming absences.' }}
        expandable={{
          rowExpandable: (r) => !!r.s,
          expandedRowRender: (r) => (
            <CoverSwapLists a={r.a} s={r.s} onlyAvailable={onlyAvailable} allSkills={allSkills} hideLeave={hideLeave}
              onOpen={canWfm ? openPerson : null} />
          ),
        }}
        columns={[
          { title: 'Date', key: 'date', width: 110, fixed: 'left',
            sorter: (x, y) => (x.s?.Start || x.a.DateStart).localeCompare(y.s?.Start || y.a.DateStart),
            defaultSortOrder: 'ascend',
            render: (_, r) => dayjs(r.s ? r.s.Start : r.a.DateStart).format('ddd MM/DD') },
          { title: 'Absent', key: 'who', width: 260,
            sorter: (x, y) => (x.a.Name || '').localeCompare(y.a.Name || ''),
            render: (_, r) => (
              <div>
                {canWfm ? <Typography.Link onClick={() => openPerson(r.a.UserID)}>{r.a.Name}</Typography.Link> : r.a.Name}
                {onLeave(r.a)}
                <div><Typography.Text style={{ fontSize: 13 }}>
                  {[r.a.TeamName, r.a.JobName].filter(Boolean).join(' / ')}
                </Typography.Text></div>
              </div>
            ) },
          { title: 'Absent Time', key: 'absentTime', width: 180,
            render: (_, r) => (
              <div>
                <Link to={`/absences/${r.a.AbsenceID}`}>{absent(r.a)}</Link>
                {r.a.Reason && <div><Typography.Text style={{ fontSize: 13 }}>{r.a.Reason}</Typography.Text></div>}
              </div>
            ) },
          // How much of this shift the absence takes, not the absence's own length.
          { title: 'Shift Time Missed', key: 'overlap', width: 90, align: 'right',
            render: (_, r) => (r.s ? fmtMins(r.s.OverlapMinutes) : '—') },
          { title: 'Shift', key: 'shift', width: 260,
            render: (_, r) => (r.s ? (
              <div>
                {fmtTime(r.s.Start)} – {fmtTime(r.s.End)}
                <div><Typography.Text style={{ fontSize: 13 }}>{r.s.Department}</Typography.Text>
                  {!r.s.DepartmentConfigured && <Tag color="orange" style={{ marginLeft: 6 }}>Not set up</Tag>}</div>
              </div>
            ) : <Typography.Text>No shift affected</Typography.Text>) },
          { title: 'Skills Needed', key: 'skills', width: 180,
            render: (_, r) => (!r.s ? '—' : r.s.RequiredSkills.length
              ? <Space size={[4, 4]} wrap>{r.s.RequiredSkills.map((k) => <Tag key={k} color="blue">{k}</Tag>)}</Space>
              : <Typography.Text>None</Typography.Text>) },
          { title: 'Could Cover', key: 'cover', width: 100, align: 'right',
            sorter: (x, y) => (x.s ? keep(x.s.Cover).length : -1) - (y.s ? keep(y.s.Cover).length : -1),
            render: (_, r) => (!r.s ? '—' : <Tag color={keep(r.s.Cover).length ? 'green' : 'red'}>{keep(r.s.Cover).length}</Tag>) },
          { title: 'Could Swap', key: 'swap', width: 100, align: 'right',
            sorter: (x, y) => (x.s ? keepSwap(x.s.Swap).length : -1) - (y.s ? keepSwap(y.s.Swap).length : -1),
            render: (_, r) => (!r.s ? '—' : <Tag color={keepSwap(r.s.Swap).length ? 'green' : 'default'}>{keepSwap(r.s.Swap).length}</Tag>) },
          ...(canPost ? [{ title: 'Forum', key: 'forum', width: 130,
            render: (_, r) => (!r.s ? null : posted.has(`${r.a.UserID}|${r.s.Start}`)
              ? <Tag color="blue">On the forum</Tag>
              : <Button size="small" onClick={() => setPosting(r)}>Post to forum</Button>) }] : []),
        ]}
      />
      <AvailabilityDrawer userId={personFor} prefs={availPrefs} onClose={() => setPersonFor(null)} onSaved={load} />
      <PublishModal data={posting ? { person: { DisplayName: posting.a.Name }, availability: posting.a.Availability } : null}
        shift={posting ? posting.s : null} userId={posting?.a.UserID} self={false} absenceId={posting?.a.AbsenceID}
        onClose={() => setPosting(null)} onDone={() => { setPosting(null); loadPosted(); }} />
    </>
  );
}
